import React, {useState} from 'react';
import {Typography, Stack, Grid, useTheme} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {
  TextFieldWrapper,
  FormHelperTextP
} from './EngageAlignSimplifyYieldComponentStyles';
import {getSearchForm} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {useSnackbar} from 'notistack';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {yupResolver} from '@hookform/resolvers/yup';
import {feedbackSchema} from '@shared/modules/public/forms/validation/FeedbackValidations';
import {useTranslation} from 'react-i18next';
import EncryptUtils from 'src/utils/EncryptUtils';
import {easyEmailService} from '@shared/services/service-handlers/PublicService';
import TextInput from '@components/form/text-input/text-input/TextInput';
import {useAppContext} from '@context/AppContext';
import {LoadingButton} from '@mui/lab';
import FormProvider from 'src/minimal-components/hook-form/form-provider';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import AuthUtils from 'src/utils/AuthUtils';
import {jwtDecode} from 'jwt-decode';
import Editor from 'src/minimal-components/editor/editor';

const EngageAlignSimplifyYieldComponent = (props: any): React.ReactElement => {
  const theme = useTheme();
  const {
    appState: {applicationName}
  } = useAppContext();

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [uiFieldsData, setUIFieldsData]: any = useState({});
  const [loader, setLoader] = React.useState(false);
  const [emailSuccess, setEmailSuccess] = React.useState(false);
  const [descriptionError, setDescriptionError] = useState<any>(null);
  const {
    handleSubmit,
    control,
    formState: {errors}
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(feedbackSchema)
  });
  const methods = useForm({});

  let userData: any = {};

  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
  }

  const init = async () => {
    setLoader(true);
    const res = await getSearchForm(
      applicationName || 'Bytepad_Legacy',
      'engage-align-simplify-yield'
    );

    const tempObj = {...res.searchForm?.fields};

    tempObj.from.value =
      userData?.user?.email || userData?.email || userData?.preferred_username;

    setUIFieldsData(tempObj);
    setLoader(false);
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFeedbackSubmit = async (data: any) => {
    if (
      !uiFieldsData.description?.value ||
      uiFieldsData.description?.value?.length < 12
    ) {
      return setDescriptionError(
        'Please enter a description of minimum 5 characters'
      );
    }
    try {
      const displayName =
        userData?.name ||
        userData?.user?.name ||
        userData?.user?.first_name + ' ' + userData?.user?.last_name;
      const email =
        userData?.user?.email ||
        userData?.preferred_username ||
        userData?.email;

      setLoader(true);
      const feedbackObj = {
        name: `${displayName} (${email})`,
        subject: data.subject,
        description: uiFieldsData?.description?.value
      };
      const encryptRequestData = EncryptUtils.encryptedData(feedbackObj);
      const res = await easyEmailService({
        tk: encryptRequestData
      });
      const resObj = JSON.parse(JSON.stringify(res));
      const {message, error} = resObj;

      if (!error) {
        setEmailSuccess(true);
        enqueueSnackbar(message, SnackBarConfig.getSuccess());
        setTimeout(() => {
          props?.setOpen(false);
        }, 5000);
      }
      setLoader(false);
    } catch (error) {
      setLoader(false);
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const handleTextChange = (value: string, key: string) => {
    setUIFieldsData((prevState: any) => {
      return {
        ...prevState,
        [key]: {
          ...prevState[key],
          value
        }
      };
    });
  };

  const renderFeedbackForm = (fields: any, control: any, errors: any) => {
    const textFields: React.ReactNode[] = [];
    for (const key in fields) {
      if (fields[key].type === 'text') {
        textFields.push(
          <TextFieldWrapper key={key}>
            <Typography
              sx={{color: 'black', fontWeight: 'bold', fontSize: '5rem'}}
              gutterBottom>
              {fields[key]?.heading[0]}
            </Typography>
            {fields[key].richText ? (
              <>
                <Editor
                  id={`easy-editor-${key}`}
                  value={fields[key].value || ''}
                  placeholder="Please Enter the feedback that you want to send us"
                  onChange={(value: string) => {
                    handleTextChange(value, key);
                    setDescriptionError(null);
                  }}
                />
                {descriptionError && (
                  <FormHelperTextP>{descriptionError}</FormHelperTextP>
                )}
              </>
            ) : (
              <Controller
                key={fields[key].name}
                name={fields[key].name}
                control={control}
                render={({field, fieldState: {error}}) => {
                  return (
                    <TextInput
                      variant="outlined"
                      id={fields[key].label}
                      label={t(fields[key].placeholder, {
                        ns: fields[key].labelNS
                      })}
                      name={fields[key].name}
                      error={!!errors[fields[key].name]}
                      helperText={errors?.[key]?.message}
                      keyPressCallBack={(e: any) =>
                        handleTextChange(e.target.value, key)
                      }
                      OnMousePDownCallBack={(e: any) => e.preventDefault()}
                      field={field}
                      defaultValue={fields[key].value}
                    />
                  );
                }}
              />
            )}
          </TextFieldWrapper>
        );
      }
    }
    return <div>{textFields}</div>;
  };

  if (emailSuccess) {
    return (
      <Stack
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="20vh"
        minWidth="20vw">
        <Typography variant="h4" sx={{fontSize: '2em'}}>
          We will reach out to you shortly
        </Typography>
      </Stack>
    );
  }

  return !loader && uiFieldsData && Object.keys(uiFieldsData).length ? (
    <Stack spacing={3} px={3}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onFeedbackSubmit)}>
        {renderFeedbackForm(uiFieldsData, control, errors)}
        <Stack spacing={3} alignItems="flex-end" sx={{mt: 1, mb: 2}}>
          <Grid item xs={12} display="flex" sm={6} md={12}>
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{
                bgcolor: theme.palette.primary.dark
              }}>
              Submit
            </LoadingButton>
          </Grid>
        </Stack>
      </FormProvider>
    </Stack>
  ) : (
    <Stack
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      minHeight={'25vh'}
      minWidth={'25vw'}>
      <LoadingScreen />
    </Stack>
  );
};

export default EngageAlignSimplifyYieldComponent;
